import MessageApi from 'dashboard/api/inbox/message';

// The create endpoint takes uploads, not URLs, so an attachment has to be
// pulled back from storage and handed over as a file.
const fetchAttachmentAsFile = async attachment => {
  const { dataUrl } = attachment;
  const response = await fetch(dataUrl);
  const blob = await response.blob();
  const name = decodeURIComponent(new URL(dataUrl).pathname.split('/').pop());

  return new File([blob], name, { type: blob.type });
};

/**
 * Copies a message into other conversations, flagged as forwarded so the
 * bubble and the channel gateway can both tell it apart. Attachments are
 * downloaded once and reused across every target.
 *
 * @param {Object} params
 * @param {Array<number>} params.conversationIds - Conversations receiving the message
 * @param {string} params.content - The message content
 * @param {Array} params.attachments - Attachments of the original message
 * @returns {Promise<{sent: number, failed: number}>} how many targets went through
 */
export const forwardMessage = async ({
  conversationIds,
  content,
  attachments = [],
}) => {
  const files = await Promise.all(attachments.map(fetchAttachmentAsFile));

  // One target failing — a closed conversation, an expired window — should not
  // hold back the others, so every result is collected before reporting back.
  const results = await Promise.allSettled(
    conversationIds.map(conversationId =>
      MessageApi.create({
        conversationId,
        message: content,
        private: false,
        contentAttributes: { forwarded: true },
        files,
      })
    )
  );

  const failed = results.filter(({ status }) => status === 'rejected').length;

  return { sent: results.length - failed, failed };
};
